import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { GetVersionsQuery, UnitSystemEnum, ShiftType, LogsTemplate } from '@/__generated__/graphql';
import { selectedWorklogsTemplate } from '../state';
import { HistoryEntry, SectionChange, FieldChange } from '../components/Panel/historyTypes';
import { getSectionUnitSymbol, convertValueByUnit, getSectionUnitForField } from '../utils';
import * as globalState from '@/components/Analysis/state';

type Version = GetVersionsQuery['allVersions'][number];
type Template = LogsTemplate | undefined;

type FieldValue = {
  fieldKey: string;
  value: number;
  createdAt?: {
    $date: string;
  };
  updatedAt?: {
    $date: string;
  };
};

type LogEntryVersionData = {
  fieldValues: FieldValue[];
  type: string | null;
  shift: ShiftType | null;
};

type FileEntry = {
  name: string;
  fileName: string;
  logDate: string;
  shift?: ShiftType;
};

const aggregateFieldValuesByField = (fieldValues: FieldValue[]): Record<string, number> => {
  const aggregated: Record<string, number> = {};
  for (const fieldValue of fieldValues) {
    aggregated[fieldValue.fieldKey] = (aggregated[fieldValue.fieldKey] || 0) + fieldValue.value;
  }
  return aggregated;
};

const getFileEntriesFromDotNotation = (data: Record<string, unknown> | null): FileEntry[] => {
  if (!data) return [];
  return Object.keys(data)
    .filter((key) => /^files\.\d+$/.test(key))
    .map((key) => data[key] as FileEntry);
};

const getAllFileEntries = (data: Record<string, unknown> | null): FileEntry[] => {
  if (!data) return [];

  if (Array.isArray(data.files)) {
    return data.files as FileEntry[];
  }

  const dotNotationFiles = getFileEntriesFromDotNotation(data);
  if (dotNotationFiles.length > 0) {
    return dotNotationFiles;
  }

  return [];
};

const transformFileVersionToHistoryEntry = (version: Version): HistoryEntry | null => {
  const isRemoval = version.lastModifiedFor === 'Attachment removed successfully';
  const updatedData = version.updatedData as Record<string, unknown> | null;
  const previousData = version.previousData as Record<string, unknown> | null;

  let documentCount = 1;
  let logDate: string | undefined;
  let shift: ShiftType | null = null;

  if (isRemoval) {
    const prevFiles = getAllFileEntries(previousData);
    const currFiles = getAllFileEntries(updatedData);
    documentCount = prevFiles.length - currFiles.length;
    if (documentCount <= 0) return null;

    if (prevFiles.length > 0) {
      const filesWithDates = prevFiles
        .filter((f) => f.logDate)
        .sort((a, b) => new Date(b.logDate).getTime() - new Date(a.logDate).getTime());
      logDate = filesWithDates[0]?.logDate;
      // Extract shift from the most recent file entry
      shift = filesWithDates[0]?.shift || null;
    }
  } else {
    const prevFiles = getAllFileEntries(previousData);
    const currFiles = getAllFileEntries(updatedData);

    const prevFileNames = new Set(prevFiles.map((f) => f.fileName).filter(Boolean));
    const newFiles = currFiles.filter((f) => !prevFileNames.has(f.fileName));

    if (newFiles.length === 0) return null;

    documentCount = newFiles.length;
    const filesWithDates = newFiles
      .filter((f) => f.logDate)
      .sort((a, b) => new Date(b.logDate).getTime() - new Date(a.logDate).getTime());
    logDate = filesWithDates[0]?.logDate;
    // Extract shift from the most recent file entry, or fall back to top-level shift
    shift = filesWithDates[0]?.shift || (updatedData?.shift as ShiftType | undefined) || null;
  }

  const timestamp = version.createdAt;

  return {
    id: `${version.id}-file`,
    timestamp,
    userName: version.lastModifiedByUser?.name || 'Unknown User',
    type: 'document_attached',
    documentAction: isRemoval ? 'removed' : 'attached',
    documentCount,
    documentDate: logDate
      ? new Date(logDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : undefined,
    shift,
  };
};

const buildHistoryEntryFromAggregated = (
  version: Version,
  template: Template,
  unitSystem: UnitSystemEnum,
  updatedAggregated: Record<string, number>,
  previousAggregated: Record<string, number>,
  shift?: ShiftType | null
): HistoryEntry | null => {
  const sections: SectionChange[] = [];
  const standaloneFields: FieldChange[] = [];

  const sectionFieldKeys = new Set<string>();
  if (template?.sections) {
    for (const section of template.sections) {
      for (const fieldKey of section.fieldKeys) {
        sectionFieldKeys.add(fieldKey);
      }
    }
  }

  // Process fields in sections
  if (template?.sections && template?.fields) {
    for (const section of template.sections) {
      const fields: FieldChange[] = [];
      let totalBefore = 0;
      let totalUpdated = 0;

      for (const fieldKey of section.fieldKeys) {
        const field = template.fields.find((f) => f.key === fieldKey);
        if (!field) continue;

        const beforeRaw = previousAggregated[fieldKey] ?? 0;
        const updatedRaw = updatedAggregated[fieldKey] ?? 0;
        const isDeleted = !(fieldKey in updatedAggregated) && beforeRaw > 0;

        // Convert values from Metric (stored) to user's unit system (for viewing)
        const before = convertValueByUnit(beforeRaw, section.sectionUnit, unitSystem, 'view');
        const updated = convertValueByUnit(updatedRaw, section.sectionUnit, unitSystem, 'view');
        const change = updated - before;

        if (change !== 0 || isDeleted) {
          const unitSymbol = getSectionUnitSymbol(section.sectionUnit, unitSystem) || '';
          fields.push({
            category: field.name,
            before,
            change,
            updated,
            unit: unitSymbol,
            deleted: isDeleted,
          });
          totalBefore += before;
          totalUpdated += updated;
        }
      }

      if (fields.length > 0) {
        const unitSymbol = getSectionUnitSymbol(section.sectionUnit, unitSystem) || '';
        const sectionDeleted = fields.every((f) => f.deleted);
        sections.push({
          sectionTitle: section.title,
          totalBefore,
          totalChange: totalUpdated - totalBefore,
          totalUpdated,
          unit: unitSymbol,
          fields,
          deleted: sectionDeleted,
        });
      }
    }
  }

  if (template?.fields) {
    for (const field of template.fields) {
      if (!field.key) continue;
      if (sectionFieldKeys.has(field.key)) continue;

      const beforeRaw = previousAggregated[field.key] ?? 0;
      const updatedRaw = updatedAggregated[field.key] ?? 0;
      const isDeleted = !(field.key in updatedAggregated) && beforeRaw > 0;

      // Get section unit for this field to apply proper conversion
      const sectionUnit = getSectionUnitForField(field.key, template.sections);

      // Convert values from Metric (stored) to user's unit system (for viewing)
      const before = convertValueByUnit(beforeRaw, sectionUnit, unitSystem, 'view');
      const updated = convertValueByUnit(updatedRaw, sectionUnit, unitSystem, 'view');
      const change = updated - before;

      if (change !== 0 || isDeleted) {
        const unitSymbol = sectionUnit ? getSectionUnitSymbol(sectionUnit, unitSystem) || '' : '';
        standaloneFields.push({
          category: field.name,
          before,
          change,
          updated,
          unit: unitSymbol,
          deleted: isDeleted,
        });
      }
    }
  }

  const allFieldKeys = new Set([
    ...Object.keys(updatedAggregated),
    ...Object.keys(previousAggregated),
  ]);

  for (const fieldKey of allFieldKeys) {
    if (sectionFieldKeys.has(fieldKey)) continue;
    if (template?.fields?.some((f) => f.key === fieldKey)) continue;

    const beforeRaw = previousAggregated[fieldKey] ?? 0;
    const updatedRaw = updatedAggregated[fieldKey] ?? 0;
    const isDeleted = !(fieldKey in updatedAggregated) && beforeRaw > 0;

    // Get section unit for this field to apply proper conversion
    const sectionUnit = getSectionUnitForField(fieldKey, template?.sections);

    // Convert values from Metric (stored) to user's unit system (for viewing)
    const before = convertValueByUnit(beforeRaw, sectionUnit, unitSystem, 'view');
    const updated = convertValueByUnit(updatedRaw, sectionUnit, unitSystem, 'view');
    const change = updated - before;

    if (change !== 0 || isDeleted) {
      const field = template?.fields?.find((f) => f.key === fieldKey);
      const unitSymbol = sectionUnit ? getSectionUnitSymbol(sectionUnit, unitSystem) || '' : '';
      standaloneFields.push({
        category: field?.name || fieldKey,
        before,
        change,
        updated,
        unit: unitSymbol,
        deleted: isDeleted,
      });
    }
  }

  if (sections.length === 0 && standaloneFields.length === 0) return null;

  const timestamp = version.createdAt;

  return {
    id: `${version.id}-worklog`,
    timestamp,
    userName:
      version.lastModifiedByUser?.name || version.lastModifiedByUser?.email || 'Unknown User',
    type: 'hours_update',
    sections: sections.length > 0 ? sections : undefined,
    standaloneFields: standaloneFields.length > 0 ? standaloneFields : undefined,
    shift,
  };
};

const transformLogEntryVersionToHistoryEntry = (
  version: Version,
  template: Template,
  unitSystem: UnitSystemEnum
): HistoryEntry | null => {
  const updatedData = version.updatedData as LogEntryVersionData | null;
  const previousData = version.previousData as LogEntryVersionData | null;

  if (!updatedData?.fieldValues || !Array.isArray(updatedData.fieldValues)) return null;

  const updatedAggregated = aggregateFieldValuesByField(updatedData.fieldValues);
  const previousAggregated = previousData?.fieldValues
    ? aggregateFieldValuesByField(previousData.fieldValues)
    : {};

  const shift = updatedData.shift;

  return buildHistoryEntryFromAggregated(
    version,
    template,
    unitSystem,
    updatedAggregated,
    previousAggregated,
    shift
  );
};

const transformWorkpackVersionToHistoryEntry = (version: Version): HistoryEntry | null => {
  // Only process file/attachment entries, not worklogs
  return transformFileVersionToHistoryEntry(version);
};

export const useKpiHistoryTransformations = () => {
  const template = useAtomValue(selectedWorklogsTemplate);
  const unitSystem = useAtomValue(globalState.unitSystem);

  const transformVersionsToHistoryEntries = useMemo(
    () => ({
      transformWorkpackVersion: (version: Version) =>
        transformWorkpackVersionToHistoryEntry(version),
      transformLogEntryVersion: (version: Version) =>
        transformLogEntryVersionToHistoryEntry(version, template, unitSystem),
    }),
    [template, unitSystem]
  );

  return transformVersionsToHistoryEntries;
};
